<template>
  <div class="change-room-approval">
    <el-tabs v-model="activeTab" @tab-click="handleTabClick">
      <!-- 待审批 -->
      <el-tab-pane label="待审批" name="approve">
        <div class="toolbar" :class="{ 'mobile-toolbar': isMobile }">
          <el-input v-model="searchApprove" placeholder="搜索姓名/房间号" prefix-icon="Search" clearable
            :style="{ width: isMobile ? '100%' : '240px' }" @clear="loadApproveList" @keyup.enter="loadApproveList" />
          <el-select v-model="filterApproveState" placeholder="状态筛选" clearable :style="{ width: isMobile ? '100%' : '140px' }"
            @change="loadApproveList">
            <el-option label="未处理" value="未处理" />
            <el-option label="已通过" value="已通过" />
            <el-option label="已拒绝" value="已拒绝" />
          </el-select>
        </div>

        <!-- PC表格 -->
        <el-table v-if="!isMobile" :data="approveList" border stripe style="width: 100%" v-loading="loadingApprove">
          <el-table-column prop="name" label="姓名" width="80" />
          <el-table-column prop="oldRoomDisplay" label="当前房间" width="120" />
          <el-table-column prop="reasonType" label="调宿类型" width="120">
            <template #default="{ row }">
              <el-tag :type="reasonTypeTag(row.reasonType)" size="small">{{ row.reasonType }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="targetRoomType" label="期望房型" width="100" />
          <el-table-column prop="reason" label="申请理由" min-width="160" show-overflow-tooltip />
          <el-table-column prop="state" label="状态" width="90">
            <template #default="{ row }">
              <el-tag :type="stateTag(row.state)" size="small">{{ row.state }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="applyTime" label="申请时间" width="130" />
          <el-table-column label="操作" width="160" fixed="right">
            <template #default="{ row }">
              <template v-if="row.state === '未处理'">
                <el-button type="success" size="small" @click="openApproveDialog(row, '已通过')">通过</el-button>
                <el-button type="danger" size="small" @click="openApproveDialog(row, '已拒绝')">拒绝</el-button>
              </template>
              <span v-else style="color: #999; font-size: 12px">{{ row.approver ? '审批人: ' + row.approver : '' }}</span>
            </template>
          </el-table-column>
        </el-table>

        <!-- 手机卡片 -->
        <div v-else class="card-list" v-loading="loadingApprove">
          <div v-for="row in approveList" :key="row.id" class="approval-card">
            <div class="card-header">
              <span class="card-name">{{ row.name }}</span>
              <el-tag :type="stateTag(row.state)" size="small">{{ row.state }}</el-tag>
              <el-tag :type="reasonTypeTag(row.reasonType)" size="small" style="margin-left: 4px">{{ row.reasonType }}</el-tag>
            </div>
            <div class="card-info">
              <div><span class="label">当前房间：</span>{{ row.oldRoomDisplay || '未分配' }}</div>
              <div><span class="label">期望房型：</span>{{ row.targetRoomType }}</div>
              <div><span class="label">申请理由：</span>{{ row.reason }}</div>
              <div><span class="label">申请时间：</span>{{ row.applyTime }}</div>
            </div>
            <div v-if="row.state === '未处理'" class="card-actions">
              <el-button type="success" size="small" @click="openApproveDialog(row, '已通过')">通过</el-button>
              <el-button type="danger" size="small" @click="openApproveDialog(row, '已拒绝')">拒绝</el-button>
            </div>
            <div v-else-if="row.approver" class="card-approver">审批人: {{ row.approver }}</div>
          </div>
          <el-empty v-if="!loadingApprove && approveList.length === 0" description="暂无待审批数据" />
        </div>

        <el-pagination v-if="approveTotal > 0" :current-page="approvePage" :page-size="10" :total="approveTotal"
          layout="total, prev, pager, next" @current-change="(p) => { approvePage = p; loadApproveList() }" />
      </el-tab-pane>

      <!-- 待分配（仅宿管和系统管理员可见） -->
      <el-tab-pane v-if="isAssignVisible" label="待分配" name="assign">
        <div class="toolbar" :class="{ 'mobile-toolbar': isMobile }">
          <el-input v-model="searchAssign" placeholder="搜索姓名/房间号" prefix-icon="Search" clearable
            :style="{ width: isMobile ? '100%' : '240px' }" @clear="loadAssignList" @keyup.enter="loadAssignList" />
        </div>

        <!-- PC表格 -->
        <el-table v-if="!isMobile" :data="assignList" border stripe style="width: 100%" v-loading="loadingAssign">
          <el-table-column prop="name" label="姓名" width="80" />
          <el-table-column prop="oldRoomDisplay" label="当前房间" width="120" />
          <el-table-column prop="reasonType" label="调宿类型" width="120">
            <template #default="{ row }">
              <el-tag :type="reasonTypeTag(row.reasonType)" size="small">{{ row.reasonType }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="targetRoomType" label="期望房型" width="100" />
          <el-table-column prop="reason" label="申请理由" min-width="160" show-overflow-tooltip />
          <el-table-column prop="state" label="状态" width="100">
            <template #default="{ row }">
              <el-tag :type="row.state === '已分配' ? 'success' : 'warning'" size="small">{{ row.state }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="approveTime" label="审批时间" width="130" />
          <el-table-column prop="approver" label="审批人" width="90" />
          <el-table-column label="操作" width="100" fixed="right">
            <template #default="{ row }">
              <el-button v-if="row.state !== '已分配'" type="primary" size="small" @click="openAssignDialog(row)">分配房间</el-button>
              <span v-else style="color: #67c23a; font-size: 12px">{{ row.newDormRoomId }}</span>
            </template>
          </el-table-column>
        </el-table>

        <!-- 手机卡片 -->
        <div v-else class="card-list" v-loading="loadingAssign">
          <div v-for="row in assignList" :key="row.id" class="approval-card">
            <div class="card-header">
              <span class="card-name">{{ row.name }}</span>
              <el-tag :type="row.state === '已分配' ? 'success' : 'warning'" size="small">{{ row.state }}</el-tag>
              <el-tag :type="reasonTypeTag(row.reasonType)" size="small" style="margin-left: 4px">{{ row.reasonType }}</el-tag>
            </div>
            <div class="card-info">
              <div><span class="label">当前房间：</span>{{ row.oldRoomDisplay || '未分配' }}</div>
              <div><span class="label">期望房型：</span>{{ row.targetRoomType }}</div>
              <div><span class="label">申请理由：</span>{{ row.reason }}</div>
              <div><span class="label">审批人：</span>{{ row.approver || '-' }}</div>
              <div v-if="row.state === '已分配'"><span class="label">已分配：</span>{{ row.newDormRoomId }}</div>
            </div>
            <div v-if="row.state !== '已分配'" class="card-actions">
              <el-button type="primary" size="small" @click="openAssignDialog(row)">分配房间</el-button>
            </div>
          </div>
          <el-empty v-if="!loadingAssign && assignList.length === 0" description="暂无待分配数据" />
        </div>

        <el-pagination v-if="assignTotal > 0" :current-page="assignPage" :page-size="10" :total="assignTotal"
          layout="total, prev, pager, next" @current-change="(p) => { assignPage = p; loadAssignList() }" />
      </el-tab-pane>
    </el-tabs>

    <!-- 审批确认弹窗 -->
    <el-dialog :title="approveDialogTitle" v-model="approveDialogVisible" :width="isMobile ? '90%' : '420px'" :close-on-click-modal="false">
      <div v-if="approveAction === '已通过'" style="margin-bottom: 12px; color: #67c23a; font-size: 14px">
        确认通过该调宿申请？通过后将由宿管分配具体房间。
      </div>
      <div v-else style="margin-bottom: 12px; color: #f56c6c; font-size: 14px">
        确认拒绝该调宿申请？
      </div>
      <el-form label-width="80px" :label-position="isMobile ? 'top' : 'right'">
        <el-form-item label="审批人">
          <el-input v-model="approverName" placeholder="请输入审批人姓名" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="approveDialogVisible = false">取消</el-button>
        <el-button :type="approveAction === '已通过' ? 'success' : 'danger'" @click="submitApprove">确认</el-button>
      </template>
    </el-dialog>

    <!-- 分配房间弹窗 -->
    <el-dialog title="分配房间" v-model="assignDialogVisible" :width="isMobile ? '90%' : '480px'" :close-on-click-modal="false">
      <div class="assign-info">
        <div><span class="label">学员：</span>{{ currentRow?.name }}</div>
        <div><span class="label">期望房型：</span>{{ currentRow?.targetRoomType }}</div>
      </div>
      <el-form label-width="80px" :label-position="isMobile ? 'top' : 'right'" style="margin-top: 16px">
        <el-form-item label="楼栋">
          <el-select v-model="assignForm.dormBuildId" placeholder="选择楼栋" style="width: 100%" @change="loadAvailableRooms">
            <el-option v-for="b in targetBuildings" :key="b.id" :label="b.label" :value="b.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="房间">
          <el-select v-model="assignForm.dormRoomId" placeholder="选择房间" style="width: 100%" :loading="loadingRooms">
            <el-option v-for="r in availableRooms" :key="r.dormRoomId" :label="r.roomLabel" :value="r.dormRoomId" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="assignDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitAssign" :loading="submitting">确认分配</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script src="../assets/js/ChangeRoomApproval.js"></script>

<style scoped>
.change-room-approval { padding: 16px; }
.toolbar { display: flex; gap: 12px; margin-bottom: 16px; }
.mobile-toolbar { flex-direction: column; }
.card-list { display: flex; flex-direction: column; gap: 12px; }
.approval-card {
  background: #fff; border-radius: 8px; padding: 14px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
}
.card-header { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.card-name { font-weight: 600; font-size: 15px; }
.card-info { font-size: 13px; color: #666; line-height: 1.8; }
.card-info .label { color: #999; }
.card-actions { margin-top: 10px; display: flex; gap: 8px; }
.card-approver { margin-top: 8px; font-size: 12px; color: #999; }
.assign-info { font-size: 14px; line-height: 1.8; color: #333; }
.assign-info .label { color: #999; }
</style>
